/**
 * PALAS Shipping Calculator
 * Calculates shipping costs based on user country/zone
 * Depends on: palas-country-detector.js (must be loaded first)
 *
 * ===== PALAS =====
 */

(function() {
  'use strict';

  // Singleton pattern
  if (window.PalasShipping) {
    return;
  }

  function nativePromotions() {
    return window.PalasPromotionsEnabled || (window.PalasPromotionsConfig && window.PalasPromotionsConfig.enabled) || (window.PalasPromotions && window.PalasPromotions.enabled);
  }
  function checkoutLabel() {
    return '—';
  }
  function promotionConfig() {
    const config = window.PalasPromotionsConfig;
    // Before the body script initializes it, the browser exposes the JSON
    // element with this ID as a named window property, not the parsed config.
    if (config && !config.nodeType) return config;
    const node = document.getElementById && document.getElementById('PalasPromotionsConfig');
    try { return node ? JSON.parse(node.textContent) : {}; }
    catch (_) { return {}; }
  }

  const PalasShipping = {
    // Configuration des zones de livraison
    config: {
      europe: {
        threshold: 70,      // Gratuit à partir de 70€
        shippingCost: 6,    // 6€ de livraison
        countries: [
          'FR', 'BE', 'BA', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'DE', 'GI', 'GR',
          'HU', 'IE', 'IT', 'XK', 'LT', 'LU', 'MK', 'MT', 'MD', 'ME', 'NL', 'NO',
          'PL', 'RO', 'SK', 'SI', 'ES', 'SE', 'CH', 'UA', 'PT', 'LV', 'MC', 'VA',
          'AX', 'BY', 'BG', 'FO', 'GG', 'IS', 'IM', 'JE', 'LI', 'SM', 'RS', 'SJ'
        ]
      },
      row: {
        threshold: 200,     // Gratuit à partir de 200 unités (devise locale)
        shippingCost: 10    // 10 unités de livraison
      }
    },

    // Cache pour éviter les recalculs
    _cachedInfo: null,

    /**
     * Détermine la zone de l'utilisateur (europe ou row)
     */
    getZone(countryCode) {
      if (!countryCode) {
        return 'europe'; // Fallback France
      }
      return this.config.europe.countries.includes(countryCode) ? 'europe' : 'row';
    },

    /**
     * Récupère les infos de shipping pour l'utilisateur actuel
     * @returns {Promise<{shippingCost: number, threshold: number, currency: string, zone: string, formattedCost: string, formattedThreshold: string}>}
     */
    async getShippingInfo() {
      if (nativePromotions()) {
        const config = promotionConfig();
        const currency = config.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
        const country = config.country || (window.Shopify && window.Shopify.country);
        const snapshot = await this._loadRates(config);
        const zones = snapshot ? snapshot.zones : [];
        const zone = zones.find(entry => entry.countries.includes(country)) || (country && zones.find(entry => entry.restOfWorld));
        const rates = zone ? zone.rates.filter(rate => rate.currency === currency) : [];
        const free = rates.filter(rate => rate.price === 0 && rate.conditions.every(c => c.operator === 'GREATER_THAN_OR_EQUAL_TO'));
        const threshold = free.length ? Math.min(...free.map(rate => Math.max(0, ...rate.conditions.map(c => c.amount)))) / 100 : null;
        const paid = rates.filter(rate => rate.price > 0);
        const shippingCost = paid.length ? Math.min(...paid.map(rate => rate.price)) / 100 : null;
        return {rates, shippingCost, threshold, currency, country, zone: zone && zone.name,
          formattedCost: shippingCost == null ? checkoutLabel() : this.formatMoney(shippingCost, currency),
          formattedThreshold: threshold == null ? '' : this.formatMoney(threshold, currency)};
      }
      // Utiliser le cache si disponible
      if (this._cachedInfo) {
        return this._cachedInfo;
      }

      // Attendre le country detector
      if (!window.PalasCountryDetector) {
        console.warn('[PalasShipping] PalasCountryDetector not found, using defaults');
        return this._getDefaultInfo();
      }

      try {
        const { country, currency } = await window.PalasCountryDetector.getUserCountry();
        const zone = this.getZone(country);
        const zoneConfig = this.config[zone];

        const info = {
          shippingCost: zoneConfig.shippingCost,
          threshold: zoneConfig.threshold,
          currency: currency || 'EUR',
          zone: zone,
          country: country,
          formattedCost: this.formatMoney(zoneConfig.shippingCost, currency || 'EUR'),
          formattedThreshold: this.formatMoney(zoneConfig.threshold, currency || 'EUR')
        };

        this._cachedInfo = info;
        return info;
      } catch (error) {
        console.error('[PalasShipping] Error getting shipping info:', error);
        return this._getDefaultInfo();
      }
    },

    /**
     * Info par défaut (Europe/EUR)
     */
    _getDefaultInfo() {
      const zoneConfig = this.config.europe;
      return {
        shippingCost: zoneConfig.shippingCost,
        threshold: zoneConfig.threshold,
        currency: 'EUR',
        zone: 'europe',
        country: null,
        formattedCost: this.formatMoney(zoneConfig.shippingCost, 'EUR'),
        formattedThreshold: this.formatMoney(zoneConfig.threshold, 'EUR')
      };
    },

    /**
     * Calcule les frais de livraison estimés pour un montant de panier
     * @param {number} cartTotal - Montant du panier (en unités, pas en centimes)
     * @returns {Promise<{shipping: number, isFree: boolean, formattedShipping: string}>}
     */
    async getEstimatedShipping(cartTotal, cart) {
      if (nativePromotions()) {
        const info = await this.getShippingInfo();
        const noDelivery = cart && cart.items && cart.items.length && cart.items.every(item => item.requires_shipping === false);
        const cents = Math.round(cartTotal * 100);
        const rates = (info.rates || []).filter(rate => rate.conditions.every(c => c.operator === 'GREATER_THAN_OR_EQUAL_TO' ? cents >= c.amount : cents <= c.amount));
        const shipping = noDelivery ? 0 : rates.length ? Math.min(...rates.map(rate => rate.price)) / 100 : null;
        const noDeliveryLabel = (document.documentElement.lang || '').startsWith('fr') ? 'Sans livraison' : 'No shipping required';
        let formattedShipping;
        if (noDelivery) formattedShipping = noDeliveryLabel;
        else if (shipping == null) formattedShipping = checkoutLabel();
        else if (shipping === 0) formattedShipping = this.getFreeLabel();
        else formattedShipping = this.formatMoney(shipping, info.currency);
        return {shipping, isFree: shipping === 0, currency: info.currency, formattedShipping};
      }
      const info = await this.getShippingInfo();
      const isFree = cartTotal >= info.threshold;
      const shipping = isFree ? 0 : info.shippingCost;

      return {
        shipping: shipping,
        isFree: isFree,
        formattedShipping: isFree ? this.getFreeLabel() : this.formatMoney(shipping, info.currency),
        currency: info.currency
      };
    },

    /**
     * Calcule le total estimé (panier + livraison)
     * @param {number} cartTotal - Montant du panier (en unités, pas en centimes)
     * @returns {Promise<{total: number, formattedTotal: string}>}
     */
    async getEstimatedTotal(cartTotal, cart) {
      const info = await this.getShippingInfo();
      const shippingResult = await this.getEstimatedShipping(cartTotal, cart);
      if (shippingResult.shipping == null) return {total: null, formattedTotal: '—'};
      const total = cartTotal + shippingResult.shipping;

      return {
        total: total,
        formattedTotal: this.formatMoney(total, info.currency)
      };
    },

    /**
     * Formate un montant dans la devise donnée
     * @param {number} amount - Montant à formater
     * @param {string} currency - Code devise (EUR, GBP, USD, etc.)
     * @returns {string}
     */
    formatMoney(amount, currency) {
      // Utiliser le formateur Shopify pour un affichage cohérent avec le reste du thème
      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
        // theme.Currency.formatMoney attend des centimes
        return theme.Currency.formatMoney(amount * 100, theme.settings.moneyFormat);
      }
      // Fallback Intl si theme.Currency pas disponible
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
          style: 'currency',
          currency: currency || 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        return amount + ' ' + (currency || 'EUR');
      }
    },

    /**
     * Retourne le label "GRATUIT" / "FREE"
     */
    getFreeLabel() {
      // Utiliser la traduction Shopify si disponible
      if (window.theme && window.theme.strings && window.theme.strings.free) {
        return window.theme.strings.free;
      }
      // Fallback basé sur la langue de la page
      const lang = document.documentElement.lang || 'fr';
      return lang.startsWith('fr') ? 'GRATUIT' : 'FREE';
    },

    /**
     * Génère le texte du tab shipping
     * Ex: "Livraison : 6 € - Gratuite dès 70 €"
     * @returns {Promise<string>}
     */
    async getShippingTabText() {
      const info = await this.getShippingInfo();
      const lang = document.documentElement.lang || 'fr';
      if (info.threshold == null) return checkoutLabel();

      if (lang.startsWith('fr')) {
        return `Livraison : ${info.formattedCost} - Gratuite dès ${info.formattedThreshold}`;
      } else {
        return `Shipping: ${info.formattedCost} - Free from ${info.formattedThreshold}`;
      }
    },

    /**
     * Invalide le cache (à appeler si la devise change)
     */
    clearCache() {
      this._cachedInfo = null;
    },

    // ===== REASSURANCE FREE SHIPPING =====

    /**
     * Met à jour les éléments "Free shipping from X" dans la réassurance
     * Cible les éléments avec l'attribut data-palas-free-shipping-item
     */
    async updateFreeShippingItems() {
      const items = document.querySelectorAll('[data-palas-free-shipping-item]');
      if (items.length === 0) return;

      const info = await this.getShippingInfo();
      const lang = document.documentElement.lang || 'fr';

      if (info.threshold == null) {
        items.forEach(item => { item.textContent = checkoutLabel(); });
        return;
      }
      // Texte dynamique basé sur la langue et le seuil
      let text;
      if (lang.startsWith('fr')) {
        text = `Livraison gratuite dès ${info.formattedThreshold}`;
      } else {
        text = `Free shipping from ${info.formattedThreshold}`;
      }

      items.forEach(item => {
        item.textContent = text;
      });
    },

    // ===== PALAS ===== The Ajax rates API requires a postcode. This reviewed
    // snapshot estimates the standard rate for the selected country, without
    // inventing an address. Shopify still calculates the final checkout price.
    async _loadRates(config) {
      if (!config.shippingRatesUrl || !config.shop) return null;
      if (!this._ratesPromise) {
        this._ratesPromise = (async () => {
          try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 8000);
            try {
              const response = await fetch(config.shippingRatesUrl, {signal: controller.signal});
              if (!response.ok) return null;
              const snapshot = await response.json();
              return snapshot.shop === config.shop && Array.isArray(snapshot.zones) ? snapshot : null;
            } finally { clearTimeout(timeout); }
          } catch (_) { return null; }
        })();
      }
      return this._ratesPromise;
    },

    // ===== CART DRAWER INTEGRATION =====

    /**
     * Met à jour les éléments du cart drawer avec le shipping estimé
     * @param {number} cartTotalCents - Total du panier en centimes (depuis cart.total_price)
     */
    async updateCartDrawer(cartTotalCents, cart, version) {
      const cartTotal = cartTotalCents / 100; // Convertir centimes en euros
      const info = await this.getShippingInfo();
      const shippingResult = await this.getEstimatedShipping(cartTotal, cart);
      const totalResult = shippingResult.shipping == null ? {formattedTotal: '—'} : {formattedTotal: this.formatMoney(cartTotal + shippingResult.shipping, info.currency)};
      if (version != null && version !== this._refreshVersion) return;

      // Mettre à jour le shipping estimé
      document.querySelectorAll('[data-estimated-shipping]').forEach(shippingEl => {
        shippingEl.textContent = shippingResult.formattedShipping;
        shippingEl.classList.toggle('palas-cart-shipping--free', shippingResult.isFree);
      });

      // Mettre à jour le total estimé
      document.querySelectorAll('[data-estimated-total]').forEach(totalEl => {
        totalEl.textContent = totalResult.formattedTotal;
      });
    },

    /**
     * Initialise les listeners pour le cart drawer
     */
    initCartDrawer() {
      // Écouter les événements de mise à jour du panier
      document.addEventListener('cart:refresh', () => this._refreshCartDrawer());
      document.addEventListener('theme:cart:change', () => this._refreshCartDrawer());
      document.addEventListener('cart:updated', () => this._refreshCartDrawer());
      document.addEventListener('palas:promotions-updated', event => {
        if (event.detail && event.detail.cart) {
          const version = this._refreshVersion = (this._refreshVersion || 0) + 1;
          this.updateCartDrawer(event.detail.cart.total_price, event.detail.cart, version);
        } else this._refreshCartDrawer();
      });

      // Initialiser au chargement si le cart drawer existe
      if (document.querySelector('[data-estimated-shipping]')) {
        this._refreshCartDrawer();
      }
    },

    /**
     * Récupère les données du panier et met à jour le drawer
     */
    async _refreshCartDrawer() {
      const version = this._refreshVersion = (this._refreshVersion || 0) + 1;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(root + 'cart.js');
        if (!response.ok) return;
        const cart = await response.json();
        await this.updateCartDrawer(cart.total_price, cart, version);
      } catch (error) {
        console.error('[PalasShipping] Error refreshing cart:', error);
      }
    }
  };

  // Exposer globalement
  window.PalasShipping = PalasShipping;

  // Auto-initialiser au DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.PalasShipping.initCartDrawer();
      window.PalasShipping.updateFreeShippingItems();
    });
  } else {
    window.PalasShipping.initCartDrawer();
    window.PalasShipping.updateFreeShippingItems();
  }

})();
